import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { localeSettingsService } from '@/services/locale-settings.service'
import { EMPTY_LOCALE_SETTINGS, localeSettingsProblems, localeSettingsReducer } from './locale-settings-rules'
import { productErrorMessage, productErrorStatus } from './product-ui'

/**
 * Tenant locale-settings screen (TO-416 product-admin-ui REQ-009): load, edit
 * under the subset rules, then PUT; a 403 renders Forbidden.
 */
export function useLocaleSettings(slug: string) {
	const { t, i18n } = useTranslation('products')
	const client = useQueryClient()
	const query = useQuery({ queryKey: ['locale-settings', slug], queryFn: () => localeSettingsService.get(slug) })
	const [state, dispatch] = useReducer(localeSettingsReducer, EMPTY_LOCALE_SETTINGS)
	const [formError, setFormError] = useState<string | null>(null)
	const [saved, setSaved] = useState(false)

	useEffect(() => {
		if (query.data) dispatch({ type: 'load', settings: query.data })
	}, [query.data])

	const save = useMutation({
		mutationFn: () =>
			localeSettingsService.save(slug, {
				enabledLocales: state.enabledLocales,
				requiredLocales: state.requiredLocales,
				defaultLocale: state.defaultLocale as string,
			}),
		onSuccess: (result) => {
			// The product editor reads the same key: it must see the saved settings.
			client.setQueryData(['locale-settings', slug], result)
			setSaved(true)
		},
		onError: (e) => setFormError(productErrorMessage(e, t, (key) => i18n.exists(key, { ns: 'products' }))),
	})
	const edit = (action: Parameters<typeof dispatch>[0]) => {
		setSaved(false)
		dispatch(action)
	}

	return {
		t,
		forbidden: productErrorStatus(query.error) === 403,
		loading: query.isPending,
		enabledLocales: state.enabledLocales,
		requiredLocales: state.requiredLocales,
		defaultLocale: state.defaultLocale,
		unconfigured: query.data?.updatedAt === null && state.enabledLocales.length === 0,
		addError: state.error ? t(state.error) : null,
		formError,
		saved,
		isBusy: save.isPending,
		onAdd: (locale: string) => edit({ type: 'add', locale }),
		onRemove: (locale: string) => edit({ type: 'remove', locale }),
		onToggleRequired: (locale: string) => edit({ type: 'toggleRequired', locale }),
		onSetDefault: (locale: string) => edit({ type: 'setDefault', locale }),
		onSave: () => {
			const problems = localeSettingsProblems(state)
			setFormError(problems[0] ? t(problems[0]) : null)
			if (problems.length === 0) save.mutate()
		},
	}
}
